import type { FlowSection, HelpSection } from '../../components/common/HelpDialog';

// Help text for the Rate Cards page and the zone page's rate-card panel; both share the pricing rules.

export const LOCK_WARNING =
  'Price changes apply to NEW bookings only. A rider who saw a price keeps it if they book within 5 minutes, unless this card is saved in between; then they are re-priced at booking.';

export const KILL_SWITCH_NOTE =
  'Kill switch: App Config RATE_CARD_PRICING_ENABLED = 0 moves NEW quotes and bookings back to the old ride-option prices within ~10 seconds, no deploy; rides already booked finish at their booked price.';

const FARE_FLOW: FlowSection = {
  title: 'How a fare is built',
  steps: [
    { label: 'Base + tiered distance slabs + time fare (booked minutes) + pickup + wait + long pickup + additions' },
    { label: 'Night % or night rates (locked at booking)', detail: 'Night window: App Config NIGHT_START_HOUR / NIGHT_END_HOUR.' },
    { label: 'Minimum fare floor, then round the total to ₹1' },
    { label: 'Zone surge multiplies the rounded total (unchanged)' },
  ],
};

export const PRICING_FLOWS: FlowSection[] = [
  {
    title: 'Which card prices a ride (decided by the pickup point)',
    steps: [
      { label: 'Zone card', detail: 'If the pickup is inside a zone that has its own card for this vehicle, that card prices the ride.' },
      { label: 'Global card', detail: 'Otherwise the vehicle\'s global card (one per vehicle type) prices it.' },
      { label: 'Old prices (legacy formula)', detail: 'Only for a vehicle with no card at all, or for NEW bookings while the kill switch is 0.' },
    ],
  },
  FARE_FLOW,
];

const UNEDITED: HelpSection = {
  title: 'Unedited cards and the old price fields',
  items: [
    'At launch every card was created as an exact copy of the old prices. Until someone saves an edit, such a card has no prices of its own: it reads them live from the old fields.',
    'An unedited GLOBAL card (chip "Today\'s prices (unchanged)") is priced from the Ride Options fare fields (base, per km, pickup, min km, waiting, night). Editing those fields on Ride Options changes these prices at once.',
    'An unedited ZONE card (outlined chip, or "Own card (old zone rates)" on the zone page) is priced from that zone\'s "Zone Pricing Overrides" values, on top of the Ride Options fields. Editing those values on the zone page changes these prices at once.',
    'The first save gives the card its own prices ("Edited · vN"). From then on the Ride Options and Zone Pricing Overrides values no longer affect it.',
    'Make price changes here, on the rate cards. The old fields stay only for unedited cards and as the kill-switch fallback.',
  ],
};

const KILL_SWITCH: HelpSection = {
  title: 'Kill switch (emergency only)',
  items: [
    KILL_SWITCH_NOTE,
    'It falls back to the OLD fields (Ride Options + Zone Pricing Overrides), not to the last saved card. Once cards are edited those old prices can be very different, so check them before switching off. Set it back to 1 to return to rate cards.',
    'For engineers: after cards have been edited, switch off first and let rides booked on edited cards finish before rolling back the backend code; the old code ignores rate cards.',
  ],
};

export const RATE_CARD_PAGE_SECTIONS: HelpSection[] = [
  {
    title: 'What a save changes',
    items: [
      'Saving a GLOBAL card changes the price in every zone that has no own card for this vehicle. Zones with their own card (the chips above the editor) keep their prices: edit those too if they should follow.',
      'Saving a ZONE card changes only that vehicle, only for pickups in that zone.',
      'New quotes and bookings use the saved card within seconds. Rides already booked keep their booked price until the end of the ride.',
      'Use the preview before saving: type a distance and compare the draft with the saved card. Nothing changes until you press Save.',
      'Every save is a new version; "History" shows who changed what and when.',
    ],
  },
  {
    title: 'Zone cards',
    items: [
      'A zone card is a full copy of a card, not a few changed fields. Later edits to the global card do not change it.',
      '"Remove override" sends that zone back to the vehicle\'s GLOBAL card for new bookings.',
      'Careful with outlined chips (made from old zone rates): Remove sends the zone to the global price, not back to its old zone price. To keep the old price, edit the values instead of removing.',
      'To make one zone price like another, open the zone\'s page and use "Copy settings from another zone".',
    ],
  },
  UNEDITED,
  KILL_SWITCH,
];

export const ZONE_PANEL_FLOWS: FlowSection[] = [
  {
    title: 'Which card prices a ride picked up in this zone',
    steps: [
      { label: 'This zone\'s own card for the vehicle', detail: 'Rows marked "Own card".' },
      { label: 'The vehicle\'s global card', detail: 'Rows marked "Uses the global card". Edited on the Rate Cards page; a change there applies here too.' },
      { label: 'Old prices (legacy formula)', detail: 'Only for a vehicle with no card at all, or for NEW bookings while the kill switch is 0.' },
    ],
  },
  FARE_FLOW,
];

export const ZONE_PANEL_SECTIONS: HelpSection[] = [
  {
    title: 'What each row means',
    items: [
      '"Own card · vN": this zone has its own edited card. Its prices are fixed by that card; global edits and the Zone Pricing Overrides below do not change it.',
      '"Own card (old zone rates)": created at launch from this zone\'s old rates and never edited. It follows the "Zone Pricing Overrides" values below (plus the Ride Options fields) live, until its first save.',
      '"Uses the global card": no card of its own. It follows the vehicle\'s global card on the Rate Cards page; if that global card is unedited, it follows the Ride Options fields.',
    ],
  },
  {
    title: 'Editing a vehicle here',
    items: [
      '"Edit / preview" opens the same editor as the Rate Cards page. With no own card yet it starts from the global card, and nothing changes until you press Save.',
      'Save creates or updates this zone\'s card for that vehicle only. New bookings in this zone use it within seconds; rides already booked keep their price.',
      '"Remove override" sends the vehicle back to the global card in this zone. For a card made from old zone rates, that means the global price, not the old zone price.',
    ],
  },
  {
    title: 'Copy settings from another zone',
    items: [
      'Overwrites THIS zone with the picked zone\'s settings for the ticked items. The picked zone is not changed.',
      'Rate cards: every vehicle gets the source zone\'s card; a vehicle with no card in the source switches to the global card here. The old zone pricing values are copied too, so the kill switch prices both zones alike.',
      'Also ticked by default: rush hour, map providers and driver subscription plan overrides. Manual surge is copied only if you tick it. Untick anything you want to keep.',
    ],
  },
  {
    title: 'The "Zone Pricing Overrides" section below',
    items: [
      'These are the old zone prices. They still set the live price of every "Own card (old zone rates)" row, and they price NEW bookings in this zone while the kill switch is 0.',
      'Once a vehicle\'s zone card has been saved here, its Zone Pricing Overrides values no longer affect it (except under the kill switch).',
    ],
  },
  KILL_SWITCH,
];
